// pages/GeneratedBrandbookPage.tsx
import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FileDown } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useNavigate } from 'react-router-dom';

import BrandbookHeader from '../components/BrandbookHeader';
import Section from '../components/GeneratedBrandbookPage/Section';
import SectionHeader from '../components/GeneratedBrandbookPage/SectionHeader';
import ColorBox from '../components/GeneratedBrandbookPage/ColorBox';
import OnThisPage from '../components/GeneratedBrandbookPage/OnThisPage';
import { useBrandBookContext } from '../context/index';
import RegenerateModal from '../components/modals/RegenerateModal';
import { SaveConfirmationModal } from '../components/modals/SaveConfirmationModal';
import { DiscardConfirmationModal } from '../components/modals/DiscardConfirmationModal';
import BrandbookSaveAs from '../components/modals/BrandbookSaveAs';
import { Button } from 'components/ui/button';


// Main component for displaying the generated brandbook content
const GeneratedBrandbookPage: React.FC = () => {
  // Context and state management
  const { formDetails, updateBrandbook, updateBrandbookSection } = useBrandBookContext();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const navigate = useNavigate();
  
  // Track currently visible section for navigation
  const [activeSection, setActiveSection] = useState<string>('brand-name');
  
  // Configuration for the regenerate modal
  const [regenerateModalConfig, setRegenerateModalConfig] = useState({
    open: false,
    title: '',
    section: 'Slogan' as 'Slogan' | 'Brand Values' | 'Vision' | 'Voice' | 'Product/Service' | 'Color Palette',
    position: { top: 0, left: 0 },
  });

  // Refs for section elements to handle scrolling and intersection observation
  const sectionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Track regenerated content for each section
  const [regeneratedContent, setRegeneratedContent] = useState<{
    [key: string]: string | null;
  }>({});
  
  // Track which sections are currently showing regenerated content
  const [regeneratedSections, setRegeneratedSections] = useState<{
    [key: string]: boolean;
  }>({});

  // Add these new state variables to the component
  const [editingSections, setEditingSections] = useState<{
    [key: string]: boolean;
  }>({});

  const [editableContent, setEditableContent] = useState<{
    [key: string]: any;
  }>({});

  // Add these state variables to the component
  const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
  const [discardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const [confirmationSection, setConfirmationSection] = useState('');
  const [saveAsModalOpen, setSaveAsModalOpen] = useState(false);

  // Set up intersection observer to track which section is currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        // Adjust rootMargin to better handle the last section
        rootMargin: '-20% 0px -33% 0px', // Changed from -80% to -50% for bottom margin
        threshold: 0.1 // Add threshold to make detection more sensitive
      }
    );

    // Observe all sections
    sectionRefs.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    // Add scroll event listener for bottom detection
    const handleScroll = () => {
      const bottom = Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight;
      if (bottom) {
        setActiveSection('color-palette');
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      sectionRefs.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handler for section navigation
  const scrollToSection = (sectionId: string) => {
    const section = sectionRefs.current.get(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Ref setter for sections
  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      sectionRefs.current.set(id, el);
    }
  };

  // Regenerate handlers for each section
  const handleRegenerateSlogan = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Slogan',
      section: 'Slogan',
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleRegenerateBrandValues = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Brand Values',
      section: 'Brand Values',
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleRegenerateVision = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Vision',
      section: 'Vision',
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleRegenerateVoice = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Voice',
      section: 'Voice',
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleRegenerateProductService = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Product/Service',
      section: 'Product/Service',
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleRegenerateColorPalette = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Color Palette',
      section: 'Color Palette',
      position: { top: rect.bottom, left: rect.left },
    });
  };

  const handleRegenerateLogo = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setRegenerateModalConfig({
      open: true,
      title: 'Logo',
      section: 'Logo' as any,
      position: { top: rect.bottom, left: rect.left },
    });
  };

  // Handle the regenerated content from the API
  const handleRegeneratedContent = (newContent: any) => {
    console.log('Regenerated content:', newContent); // Debug log
    
    const sectionKey = regenerateModalConfig.section.toLowerCase().replace(/[^a-z]/g, '');
    
    // Handle different response formats
    let contentToStore;
    if (typeof newContent === 'string') {
      // Direct string response
      contentToStore = newContent;
    } else if (newContent && newContent.regenerated_section) {
      // Object with regenerated_section property
      contentToStore = newContent.regenerated_section;
    } else {
      // Other object format
      contentToStore = newContent;
    }
    
    setRegeneratedContent({
      ...regeneratedContent,
      [sectionKey]: contentToStore
    });
    
    setRegeneratedSections({
      ...regeneratedSections,
      [sectionKey]: true
    });

    // For logo section, we need to handle the logo_url specifically
    if (sectionKey === 'logo') {
      // Extract the logo_url from the regenerated content
      const logoUrl = typeof contentToStore === 'object' ? contentToStore.logo_url : contentToStore;
      
      // Update the brandbook with the new logo URL
      updateBrandbookSection('logo_url', logoUrl);
    }
  };

  // Save handler for regenerated content
  const handleSaveRegenerated = (section: string) => {
    setConfirmationSection(section);
    setSaveConfirmationOpen(true);
  };

  // Discard handler for regenerated content
  const handleDiscardRegenerated = (section: string) => {
    setConfirmationSection(section);
    setDiscardConfirmationOpen(true);
  };

  // Add these new handler functions
  const handleEditSection = (sectionKey: string, currentContent: any) => {
    // Set the section to editing mode
    setEditingSections(prev => ({
      ...prev,
      [sectionKey]: true
    }));
    
    // Initialize editable content with current content
    setEditableContent(prev => ({
      ...prev,
      [sectionKey]: JSON.parse(JSON.stringify(currentContent)) // Deep copy
    }));
  };

  // Update the save and discard handlers
  const handleSaveEdited = (section: string) => {
    setConfirmationSection(section);
    setSaveConfirmationOpen(true);
  };

  const handleDiscardEdited = (section: string) => {
    setConfirmationSection(section);
    setDiscardConfirmationOpen(true);
  };

  // Add these new handlers for confirmation actions
  const confirmSaveEdited = () => {
    const section = confirmationSection;
    
    // Check if we're saving edited content
    if (editingSections[section]) {
      const content = editableContent[section];
      
      if (content) {
        updateBrandbookSection(section, content);
        setEditingSections(prev => ({
          ...prev,
          [section]: false
        }));
        toast.success(`${section} updated successfully`);
      }
    } 
    // Check if we're saving regenerated content
    else if (regeneratedSections[section]) {
      const content = regeneratedContent[section];
      
      if (content) {
        updateBrandbookSection(section, content);
        setRegeneratedContent(prev => ({
          ...prev,
          [section]: null
        }));
        setRegeneratedSections(prev => ({
          ...prev,
          [section]: false
        }));
        toast.success(`${section} updated successfully`);
      }
    }
  };

  const confirmDiscardEdited = () => {
    const section = confirmationSection;
    
    // Check if we're discarding edited content
    if (editingSections[section]) {
      setEditingSections(prev => ({
        ...prev,
        [section]: false
      }));
      
      // Clear editable content
      setEditableContent(prev => ({
        ...prev,
        [section]: null
      }));
    } 
    // Check if we're discarding regenerated content
    else if (regeneratedSections[section]) {
      setRegeneratedContent(prev => ({
        ...prev,
        [section]: null
      }));
      setRegeneratedSections(prev => ({
        ...prev,
        [section]: false
      }));
    }
  };

  // Handler for updating editable content
  const handleEditableContentChange = (sectionKey: string, newContent: any) => {
    setEditableContent(prev => ({
      ...prev,
      [sectionKey]: newContent
    }));
  };

  // Add edit handlers for each section
  const handleEditSlogan = (e: React.MouseEvent) => {
    if (formDetails.brandbook) {
      handleEditSection('slogan', formDetails.brandbook.slogan);
    }
  };

  const handleEditBrandValues = (e: React.MouseEvent) => {
    if (formDetails.brandbook) {
      handleEditSection('brandvalues', formDetails.brandbook.brandValues);
    }
  };

  const handleEditVision = (e: React.MouseEvent) => {
    if (formDetails.brandbook) {
      handleEditSection('vision', formDetails.brandbook.vision);
    }
  };

  const handleEditVoice = (e: React.MouseEvent) => {
    if (formDetails.brandbook) {
      handleEditSection('voice', formDetails.brandbook.voice);
    }
  };

  const handleEditProductService = (e: React.MouseEvent) => {
    if (formDetails.brandbook) {
      handleEditSection('productservice', formDetails.brandbook.productService);
    }
  };

  // Modify the save click handler to store brandbook in localStorage when not authenticated
  const handleSaveClick = () => {
    if (!isAuthenticated) {
      // Store the current brandbook in localStorage before redirecting
      if (formDetails.brandbook) {
        localStorage.setItem('tempBrandbook', JSON.stringify(formDetails.brandbook));
        toast.error(
          <div className="flex flex-col">
            <span>Please login or sign up to save your brandbook</span>
            <button 
              className="text-sm text-blue-500 hover:text-blue-700 underline mt-1 self-start"
              onClick={() => navigate('/auth/login')}
            >
              Go to login
            </button>
          </div>,
          { duration: 5000 }
        );
      }
      return;
    }
    setSaveAsModalOpen(true);
  };

  // Redirect if no brandbook data
  if (!formDetails.brandbook) {
    return (
      <div className="flex flex-col gap-4 justify-center items-center min-h-screen text-text-primary">
        <p className="text-lg">No brandbook data available. Please generate a brandbook first.</p>
        <Button variant="customPrimary" onClick={() => navigate('/brandbook/get-started')}> Generate Brandbook </Button>
      </div>
    );
  }

  const brandbook = formDetails.brandbook;

  // Custom renderer for brand values section
  const renderBrandValues = (content: any[]) => (
    <div className="space-y-6 p-0">
      {content.map((value, index) => (
        <div key={index} className="space-y-2">
          <h3 className="font-bold text-xl">
            {value.heading}: <span className='text-lg font-medium'>{value.subHeading}</span>
          </h3>
          <p>{value.description}</p>
        </div>
      ))}
    </div>
  );

  // Add custom renderers for each section type
  const renderVision = (content: any) => (
    <div className="space-y-4 p-0">
      <h3 className="font-bold">{content.oneLiner}</h3>
      <p>{content.description}</p>
    </div>
  );

  const renderVoice = (content: any) => (
    <div className="space-y-4 p-0">
      <h3 className="font-bold">{content.oneLiner}</h3>
      <p>{content.description}</p>
    </div>
  );

  const renderProductService = (content: string[]) => (
    <div className="space-y-2 p-0">
      {content.map((service, index) => (
        <p key={index}>{service}</p>
      ))}
    </div>
  );

  const renderColorPalette = (content: string[]) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:justify-between gap-4 p-0">
      {content.map((color, index) => (
        <ColorBox key={index} color={color} />
      ))}
    </div>
  );

  const renderSlogan = (content: string) => (
    <h2 className="text-xl font-medium">
      {content}
    </h2>
  );

  const renderLogo = (content: any) => (
    <div className="p-0">
      <div className="flex flex-col items-center gap-4">
        <img 
          src={content.logo_url} 
          alt="Brand Logo" 
          className="max-w-[200px] h-auto rounded-md shadow-md"
        />
        <p className="text-sm text-text-secondary">
          Generated logo for {brandbook.companyName}
        </p>
      </div>
    </div>
  );

  // Create render functions for editable content
  const renderEditableSlogan = (content: string, onChange: (newContent: string) => void) => (
    <div className="p-0">
      <textarea
        className="w-full bg-background p-2 text-xl font-medium border rounded-md"
        value={content}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
      />
    </div>
  );

  const renderEditableBrandValues = (content: any[], onChange: (newContent: any[]) => void) => (
    <div className="space-y-6 p-0">
      {content.map((value, index) => (
        <div key={index} className="space-y-2">
          <div className="flex gap-2">
            <input
              className="font-bold text-xl border rounded-md p-1 w-1/3"
              value={value.heading}
              onChange={(e) => {
                const newValues = [...content];
                newValues[index] = { ...newValues[index], heading: e.target.value };
                onChange(newValues);
              }}
            />
            <input
              className="text-lg font-medium border rounded-md p-1 w-2/3"
              value={value.subHeading}
              onChange={(e) => {
                const newValues = [...content];
                newValues[index] = { ...newValues[index], subHeading: e.target.value };
                onChange(newValues);
              }}
            />
          </div>
          <textarea
            className="w-full p-2 border rounded-md"
            value={value.description}
            onChange={(e) => {
              const newValues = [...content];
              newValues[index] = { ...newValues[index], description: e.target.value };
              onChange(newValues);
            }}
            rows={3}
          />
        </div>
      ))}
    </div>
  );

  const renderEditableVision = (content: any, onChange: (newContent: any) => void) => (
    <div className="space-y-4 p-0">
      <input
        className="w-full p-2 font-bold border rounded-md"
        value={content.oneLiner}
        onChange={(e) => onChange({ ...content, oneLiner: e.target.value })}
      />
      <textarea
        className="w-full p-2 border rounded-md"
        value={content.description}
        onChange={(e) => onChange({ ...content, description: e.target.value })}
        rows={4}
      />
    </div>
  );

  const renderEditableVoice = (content: any, onChange: (newContent: any) => void) => (
    <div className="space-y-4 p-0">
      <input
        className="w-full p-2 font-bold border rounded-md"
        value={content.oneLiner}
        onChange={(e) => onChange({ ...content, oneLiner: e.target.value })}
      />
      <textarea
        className="w-full p-2 border rounded-md"
        value={content.description}
        onChange={(e) => onChange({ ...content, description: e.target.value })}
        rows={4}
      />
    </div>
  );

  const renderEditableProductService = (content: string[], onChange: (newContent: string[]) => void) => (
    <div className="space-y-2 p-0">
      {content.map((service, index) => (
        <div key={index} className="flex gap-2">
          <input
            className="w-full p-2 border rounded-md"
            value={service}
            onChange={(e) => {
              const newServices = [...content];
              newServices[index] = e.target.value;
              onChange(newServices);
            }}
          />
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => {
              const newServices = [...content];
              newServices.splice(index, 1);
              onChange(newServices);
            }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        className="text-blue-500 hover:text-blue-700"
        onClick={() => onChange([...content, ''])}
      >
        + Add service
      </button>
      </div>
    );

  return (
    <div className="flex flex-col bg-background-main h-full overflow-x-hidden">
      <BrandbookHeader
        title={`${formDetails.companyName || 'Brand'}`}
        onExport={() => {
          window.history.back();
        }}
        onSaveClick={handleSaveClick}
      />

      {/* Create a container for sticky behavior */}
      <div className="sticky top-0 bg-background-main z-50 lg:hidden">
        <OnThisPage 
          activeSection={activeSection} 
          onSectionClick={scrollToSection}
        />
      </div>
      
      {/* Add padding bottom to prevent content from being hidden behind fixed buttons */}
      <div className="container mx-auto px-4 py-8 pb-24 lg:pb-8">
        <div className="flex flex-col lg:flex-row lg:space-x-8">
          {/* Main Content */}
          <div className="w-full lg:w-3/4">
            <div id="brand-name" ref={setRef('brand-name')} className="mb-8">
              <SectionHeader title="BRAND NAME" showRegenerateIcon={false} showEditIcon={false} />
              <h1 className="text-4xl font-bold bg-background p-6 text-text-primary rounded-lg">
                {formDetails.companyName}
              </h1>
            </div>

            <Section 
              id="slogan" 
              title="SLOGAN" 
              ref={setRef('slogan')}
              onRegenerate={handleRegenerateSlogan}
              onEdit={handleEditSlogan}
              regeneratedContent={regeneratedContent['slogan']}
              isRegenerated={regeneratedSections['slogan']}
              isEditing={editingSections['slogan']}
              onSaveRegenerated={() => handleSaveRegenerated('slogan')}
              onDiscardRegenerated={() => handleDiscardRegenerated('slogan')}
              onSaveEdited={() => handleSaveEdited('slogan')}
              onDiscardEdited={() => handleDiscardEdited('slogan')}
              renderRegeneratedContent={renderSlogan}
              renderEditableContent={renderEditableSlogan}
              editableContent={editableContent['slogan']}
              onEditableContentChange={(newContent) => handleEditableContentChange('slogan', newContent)}
            >
              <h2 className="text-xl font-medium">
                {brandbook.slogan}
              </h2>
            </Section>

            <Section
              id="brand-values"
              title="BRAND VALUES"
              ref={setRef('brand-values')}
              onRegenerate={handleRegenerateBrandValues}
              onEdit={handleEditBrandValues}
              regeneratedContent={regeneratedContent['brandvalues']}
              isRegenerated={regeneratedSections['brandvalues']}
              isEditing={editingSections['brandvalues']}
              onSaveRegenerated={() => handleSaveRegenerated('brandvalues')}
              onDiscardRegenerated={() => handleDiscardRegenerated('brandvalues')}
              onSaveEdited={() => handleSaveEdited('brandvalues')}
              onDiscardEdited={() => handleDiscardEdited('brandvalues')}
              renderRegeneratedContent={renderBrandValues}
              renderEditableContent={renderEditableBrandValues}
              editableContent={editableContent['brandvalues']}
              onEditableContentChange={(newContent) => handleEditableContentChange('brandvalues', newContent)}
            >
              <div className="space-y-6">
                {brandbook.brandValues.map((value, index) => (
                    <div key={index} className="space-y-2">
                    <h3 className="font-bold text-xl">
                      {value.heading}: <span className='text-lg font-medium'>{value.subHeading}</span>
                      </h3>
                    <p>{value.description}</p>
                  </div>
                ))}
                    </div>
            </Section>

            <Section 
              id="vision" 
              title="VISION" 
              ref={setRef('vision')}
              onRegenerate={handleRegenerateVision}
              onEdit={handleEditVision}
              regeneratedContent={regeneratedContent['vision']}
              isRegenerated={regeneratedSections['vision']}
              isEditing={editingSections['vision']}
              onSaveRegenerated={() => handleSaveRegenerated('vision')}
              onDiscardRegenerated={() => handleDiscardRegenerated('vision')}
              onSaveEdited={() => handleSaveEdited('vision')}
              onDiscardEdited={() => handleDiscardEdited('vision')}
              renderRegeneratedContent={renderVision}
              renderEditableContent={renderEditableVision}
              editableContent={editableContent['vision']}
              onEditableContentChange={(newContent) => handleEditableContentChange('vision', newContent)}
            >
              <div className="space-y-4 p-0">
                <h3 className="font-bold">{brandbook.vision.oneLiner}</h3>
                <p>{brandbook.vision.description}</p>
              </div>
            </Section>

            <Section
              id="voice" 
              title="VOICE" 
              ref={setRef('voice')}
              onRegenerate={handleRegenerateVoice}
              onEdit={handleEditVoice}
              regeneratedContent={regeneratedContent['voice']}
              isRegenerated={regeneratedSections['voice']}
              isEditing={editingSections['voice']}
              onSaveRegenerated={() => handleSaveRegenerated('voice')}
              onDiscardRegenerated={() => handleDiscardRegenerated('voice')}
              onSaveEdited={() => handleSaveEdited('voice')}
              onDiscardEdited={() => handleDiscardEdited('voice')}
              renderRegeneratedContent={renderVoice}
              renderEditableContent={renderEditableVoice}
              editableContent={editableContent['voice']}
              onEditableContentChange={(newContent) => handleEditableContentChange('voice', newContent)}
            >
              <div className="space-y-4 p-0">
                <h3 className="font-bold">{brandbook.voice.oneLiner}</h3>
                <p>{brandbook.voice.description}</p>
              </div>
            </Section>

            <Section
              id="product-service"
              title="PRODUCT/SERVICE"
              ref={setRef('product-service')}
              onRegenerate={handleRegenerateProductService}
              onEdit={handleEditProductService}
              regeneratedContent={regeneratedContent['productservice']}
              isRegenerated={regeneratedSections['productservice']}
              isEditing={editingSections['productservice']}
              onSaveRegenerated={() => handleSaveRegenerated('productservice')}
              onDiscardRegenerated={() => handleDiscardRegenerated('productservice')}
              onSaveEdited={() => handleSaveEdited('productservice')}
              onDiscardEdited={() => handleDiscardEdited('productservice')}
              renderRegeneratedContent={renderProductService}
              renderEditableContent={renderEditableProductService}
              editableContent={editableContent['productservice']}
              onEditableContentChange={(newContent) => handleEditableContentChange('productservice', newContent)}
            >
              <div className="space-y-2 p-0">
                {brandbook.productService.map((service, index) => (
                  <p key={index}>{service}</p>
                ))}
              </div>
            </Section>

            {/* Add new Logo section */}
            {brandbook.logo_url && (
              <Section
                id="logo"
                title="LOGO"
                ref={setRef('logo')}
                onRegenerate={handleRegenerateLogo}
                regeneratedContent={regeneratedContent['logo']}
                isRegenerated={regeneratedSections['logo']}
                onSaveRegenerated={() => handleSaveRegenerated('logo')}
                onDiscardRegenerated={() => handleDiscardRegenerated('logo')}
                renderRegeneratedContent={renderLogo}
                showEditIcon={false}
              >
                <div className="p-0">
                  <div className="flex flex-col items-center gap-4">
                  <img
                    src={brandbook.logo_url}
                    alt="Brand Logo"
                      className="max-w-[200px] h-auto rounded-md shadow-md"
                  />
                    <p className="text-sm text-text-secondary">
                      Generated logo for {brandbook.companyName}
                    </p>
                  </div>
                </div>
              </Section>
            )}

            <Section
              id="color-palette"
              title="COLOR PALETTE"
              ref={setRef('color-palette')}
              onRegenerate={handleRegenerateColorPalette}
              regeneratedContent={regeneratedContent['colorpalette']}
              isRegenerated={regeneratedSections['colorpalette']}
              onSaveRegenerated={() => handleSaveRegenerated('colorpalette')}
              onDiscardRegenerated={() => handleDiscardRegenerated('colorpalette')}
              renderRegeneratedContent={renderColorPalette}
              showEditIcon={false}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:flex lg:justify-between gap-4 p-0">
                {brandbook.colorPalette.map((color, index) => (
                  <ColorBox key={index} color={color} />
                ))}
              </div>
            </Section>
          </div>

          {/* Desktop Navigation */}
          <aside className="hidden lg:block lg:w-1/4">
            <div className="sticky top-8">
              <OnThisPage 
                activeSection={activeSection} 
                onSectionClick={scrollToSection}
              />
            </div>
          </aside>
        </div>
      </div>

      {/* Fixed bottom buttons - visible only on mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background-main border-t lg:hidden flex gap-3">
        <div className="relative flex-1">
          {/* Coming Soon Badge */}
          <div className="absolute -top-2 -right-2 bg-blue-600 text-white px-1 py-1 rounded-full text-[6px] z-10 font-medium whitespace-nowrap">
            Coming Soon
          </div>
          <Button
            variant="ghost"
            className="w-full text-base text-primary border border-primary disabled:opacity-50"
            disabled={true}
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
        <Button
          variant="customPrimary"
          className="flex-1"
          onClick={handleSaveClick}
        >
          Save
        </Button>
      </div>

      <RegenerateModal
        open={regenerateModalConfig.open}
        onOpenChange={(open) => setRegenerateModalConfig(prev => ({ ...prev, open }))}
        title={regenerateModalConfig.title}
        section={regenerateModalConfig.section}
        brandBook={brandbook}
        onRegenerate={handleRegeneratedContent}
        position={regenerateModalConfig.position}
      />
      
      {/* Save confirmation modal */}
      <SaveConfirmationModal
        open={saveConfirmationOpen}
        onOpenChange={setSaveConfirmationOpen}
        onConfirm={confirmSaveEdited}
        section={confirmationSection}
      />
      
      {/* Discard confirmation modal */}
      <DiscardConfirmationModal
        open={discardConfirmationOpen}
        onOpenChange={setDiscardConfirmationOpen}
        onConfirm={confirmDiscardEdited}
        section={confirmationSection}
      />

      <BrandbookSaveAs
        open={saveAsModalOpen}
        onOpenChange={setSaveAsModalOpen}
        brandbook={{
          companyName: brandbook.companyName || '',
          slogan: brandbook.slogan || '',
          brandValues: brandbook.brandValues || [],
          vision: brandbook.vision || { oneLiner: '', description: '' },
          voice: brandbook.voice || { oneLiner: '', description: '' },
          productService: brandbook.productService || [],
          colorPalette: brandbook.colorPalette || [],
          suggestedFont: brandbook.suggestedFont || '',
          logo_url: brandbook.logo_url || ''
        }}
      />
    </div>
  );
};

export default GeneratedBrandbookPage;